import classNames from 'classnames'
import { useCallback, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { CascadingMenuOption } from '@opallabs/concepts'
import { Button, ButtonIconPosition } from '../Button'
import * as CascadingMenu from '../CascadingMenu'
import type { CascadingMenuProps } from '../CascadingMenu/Root'
import { InputLabel } from '../InputLabel'
import { Popover } from '../Popover'
import { inputContainer as singleSelectTriggerStyles } from '../SingleSelect/composableComponents/Trigger/styles'
import * as styles from './styles'

export type CascadingSingleSelectProps = Omit<
  CascadingMenuProps,
  'onSelect' | 'onActiveDescendentChange' | 'onCloseMenu'
> & {
  label?: string
  /**
   * Item will be null when deselecting to match
   * pattern used in standard SingleSelect
   */
  onSelect: (item: CascadingMenuOption | null) => void
  placeholderText?: string
  selectedItem?: CascadingMenuOption
  zIndex?: number
}

const listIdBase = 'cascading-single-select'
const labelIdBase = 'cascading-single-select-label'

/**
 * Popover wrapper around the cascading menu that applies the appropriate
 * styles and a11y attributes to be used as a single select input
 */
export const CascadingSingleSelect: React.FC<CascadingSingleSelectProps> = ({
  className,
  initialSelection,
  items,
  label,
  onSelect,
  placeholderText = 'Select...',
  selectedItem,
  zIndex,
  ...cascadingMenuProps
}) => {
  const [activeDescendant, setActiveDescendant] = useState<string | undefined>()
  const [open, setOpen] = useState(false)

  const inputId = useRef(`${listIdBase}-${uuidv4()}`)
  const labelId = useRef(`${labelIdBase}-${uuidv4()}`)

  // This is managed manually instead of passing to `handleOpenChange`
  // on Popover.Root to avoid the popover closing prematurely
  // while ascending or descending the tree
  const handleOpenChange = useCallback((open: boolean) => {
    setOpen(open)
    if (!open) setActiveDescendant(undefined)
  }, [])

  const handleOnSelect = useCallback(
    (item: CascadingMenuOption) => {
      handleOpenChange(false)
      setActiveDescendant(undefined)

      if (item.value === selectedItem?.value) return onSelect(null)

      onSelect(item)
    },
    [handleOpenChange, onSelect, selectedItem]
  )

  return (
    <div
      className={classNames(styles.selectors.root, className, {
        [styles.states.hasSelection]: Boolean(selectedItem),
      })}
      css={styles.container}
    >
      <InputLabel id={labelId.current} text={label} />
      <Popover.Root open={open}>
        <Popover.Trigger>
          <Button
            aria-activedescendant={activeDescendant}
            aria-controls={inputId.current}
            aria-expanded={open}
            aria-haspopup="tree"
            aria-labelledby={label ? labelId.current : undefined}
            aria-pressed={undefined}
            buttonText={
              selectedItem?.label ?? initialSelection?.label ?? placeholderText
            }
            // singleSelectTriggerStyles pulled from SingleSelect.Trigger to keep parity
            css={[singleSelectTriggerStyles, styles.button]}
            displayMenuIndicator={true}
            menuIndicatorPosition={ButtonIconPosition.trailing}
            name="Select..."
            onClick={() => handleOpenChange(!open)}
            role="combobox"
            variant="ghost"
          />
        </Popover.Trigger>
        <Popover.Content
          css={styles.menu}
          omitPortal={true}
          onEscapeKeyDown={() => handleOpenChange(false)}
          onPointerDownOutside={() => handleOpenChange(false)}
          zIndex={zIndex}
        >
          <CascadingMenu.Root
            {...cascadingMenuProps}
            initialSelection={initialSelection}
            items={items}
            onActiveDescendentChange={setActiveDescendant}
            onCloseMenu={() => handleOpenChange(false)}
            onSelect={handleOnSelect}
          >
            <CascadingMenu.Search includeNestedOptions={true} />
            <CascadingMenu.SearchableItems
              multiselectable={false}
              itemListId={inputId.current}
            />
          </CascadingMenu.Root>
        </Popover.Content>
      </Popover.Root>
    </div>
  )
}
